import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import { formatZipCode } from 'src/helper/utils';

// The API sends `state` as a StateResource collection (an array with one entry,
// or empty when the stored state code resolves to no known state); the slice
// holds the single selected option.
const normalizeStateOption = (
  state: OnboardingDataAPIType['basic-info']['state'],
): { value: string; label: string } | undefined => (Array.isArray(state) ? state[0] : state);

import {
  getOnboardingDetails,
  setBasicInfo,
  setOnboardingStatus,
  setPassword,
  setServiceArea,
  setServices,
} from './api';
import {
  ONBOARDING_STEPS,
  type OnboardingDataAPIType,
  type OnboardingStateType,
  type OnboardingStatusAPIType,
  type ServiceAreaDataType,
  type ServiceOfferedDataType,
} from './types';

const extraReducers = (builder: ActionReducerMapBuilder<OnboardingStateType>) => {
  builder.addCase(getOnboardingDetails().pending, (state: OnboardingStateType) => {
    state.isLoading = true;
  });

  builder.addCase(
    getOnboardingDetails().fulfilled,
    (state: OnboardingStateType, action: PayloadAction<OnboardingDataAPIType>) => {
      const { 'basic-info': basicInfo } = action.payload;
      state.basicInfo = {
        address1: basicInfo.address1,
        address2: basicInfo.address2,
        city: basicInfo.city,
        company: basicInfo.company_name,
        email: basicInfo.email,
        firstName: basicInfo.first_name,
        lastName: basicInfo.last_name,
        phoneNumber: basicInfo.phone_number,
        service: basicInfo.service,
        state: normalizeStateOption(basicInfo.state),
        vendorId: basicInfo.vendor_uuid,
        zip: formatZipCode(basicInfo.postal_zip_code),
        insuranceExpiry: basicInfo.gl_insurance_expire_at ?? null,
        isSynced: basicInfo.is_synced ?? false,
        agreeToTexts: basicInfo.agree_to_texts ?? false,
      };
      state.isLoading = false;
      state.onboardingId = action.payload.vendor_onboarding_uuid;
      state.serviceArea = action.payload['service-area'];
      state.serviceOffered = action.payload['service-offered'];
      state.type = action.payload.type;
      state.step = action.payload.status.slug;
      state.locationAPIKey = action.payload['location-api-key'];
    },
  );

  builder.addCase(getOnboardingDetails().rejected, (state: OnboardingStateType) => {
    state.isLoading = false;
  });

  builder.addCase(
    setOnboardingStatus().fulfilled,
    (state: OnboardingStateType, action: PayloadAction<{ status: OnboardingStatusAPIType }>) => {
      state.step = action.payload.status.slug;
    },
  );

  builder.addCase(
    setBasicInfo().fulfilled,
    (state: OnboardingStateType, action: PayloadAction<OnboardingDataAPIType>) => {
      const { 'basic-info': basicInfo } = action.payload;
      state.basicInfo = {
        address1: basicInfo.address1,
        address2: basicInfo.address2,
        city: basicInfo.city,
        company: basicInfo.company_name,
        email: basicInfo.email,
        firstName: basicInfo.first_name,
        lastName: basicInfo.last_name,
        phoneNumber: basicInfo.phone_number,
        service: basicInfo.service,
        state: normalizeStateOption(basicInfo.state),
        vendorId: basicInfo.vendor_uuid,
        zip: basicInfo.postal_zip_code,
        insuranceExpiry: basicInfo.gl_insurance_expire_at ?? null,
        isSynced: basicInfo.is_synced ?? false,
        agreeToTexts: basicInfo.agree_to_texts ?? false,
      };
      state.step = action.payload.status.slug;
    },
  );

  builder.addCase(setPassword().fulfilled, (state: OnboardingStateType) => {
    state.step = ONBOARDING_STEPS.BASIC_INFO;
  });

  builder.addCase(
    setServices().fulfilled,
    (state: OnboardingStateType, action: PayloadAction<ServiceOfferedDataType>) => {
      const serviceOffered = action.payload;
      state.serviceOffered = { ...serviceOffered };
      state.step = ONBOARDING_STEPS.SERVICE_AREA;
    },
  );

  builder.addCase(
    setServiceArea().fulfilled,
    (state: OnboardingStateType, action: PayloadAction<ServiceAreaDataType>) => {
      const serviceArea = action.payload;
      state.serviceArea = { ...serviceArea };
      state.step = ONBOARDING_STEPS.UNDER_REVIEW;
    },
  );
};

export default extraReducers;
